import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { slideFileFromSlide, tileExtensionFromSlideFile } from "../showTile/tileUrl";
import { CoverLoader, GeneratedCover, LoadingImage } from "./CoverLoader";
import { resolveSlideTileCover, slideOwnFileUrls } from "./slideTileCover";

const RASTER_RE = /\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i;

/*
  Same cover tile as the mobile app (`slideThumbnailUrlFromSlide`):
  `{base}/3/3/3.{ext}` — a mid-pyramid crop. Fallbacks for shallow pyramids.
*/
const APP_COVER_PATHS = ["3/3/3", "2/1/1", "2/0/0", "1/0/0", "0/0/0"];

function probeImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => resolve(null);
    img.referrerPolicy = "no-referrer";
    img.src = url;
  });
}

function slideFiles(slide) {
  const files = slide?.Files ?? slide?.files ?? [];
  return Array.isArray(files) ? files.filter(Boolean) : [];
}

const isRaster = (f) =>
  String(f?.type || "").startsWith("image/") || RASTER_RE.test(String(f?.url || f?.path || ""));

/** App-matching cover from a deep-zoom tile root. */
async function tileCover(file) {
  const base = String(file.url).replace(/\/+$/, "");
  const guess = tileExtensionFromSlideFile(file);
  const exts = [...new Set([guess, "jpg", "png", "webp"])];
  for (const ext of exts) {
    for (const path of APP_COVER_PATHS) {
      // eslint-disable-next-line no-await-in-loop
      const src = await probeImage(`${base}/${path}.${ext}`);
      if (src) {
        return { kind: "image", src, zoomed: path !== "0/0/0" };
      }
    }
  }
  return null;
}

/**
 * Cover priority: the attached cover image, then the slide's zoomed
 * overview tiles (app 3/3/3), then any raster file attached to the slide.
 */
async function resolveCover(slide, preferredUrl) {
  // A preferredUrl that is just one of the slide's own files is usually the
  // full-resolution upload: try small tiles before downloading it.
  const ownUrls = [...slideOwnFileUrls(slide)];
  const preferredIsOwnFile =
    !!preferredUrl && ownUrls.some((u) => preferredUrl === u || preferredUrl.endsWith(u));
  if (preferredUrl && !preferredIsOwnFile && (await probeImage(preferredUrl))) {
    return { kind: "image", src: preferredUrl, zoomed: true };
  }
  const files = slideFiles(slide);
  const tileRoot = files.find((f) => f?.url && !isRaster(f));
  if (tileRoot) {
    const cover = await tileCover(tileRoot);
    if (cover) return cover;
  }
  const tiles = await resolveSlideTileCover(slide);
  if (tiles) return tiles;
  if (preferredIsOwnFile && (await probeImage(preferredUrl))) {
    return { kind: "image", src: preferredUrl, zoomed: true };
  }
  for (const f of files.filter(isRaster)) {
    const src = await probeImage(f.url || f.path);
    if (src) return { kind: "image", src, zoomed: true };
  }
  return null;
}

// Resolved covers survive remounts (scrolling, tab switches) so cards don't re-probe.
// Bump when cover selection rules change so stale overview tiles are not reused.
const COVER_CACHE_VERSION = "app-3-3-3-v1";
const coverCache = new Map();

/** Starts as false and flips once the element comes within `margin` of the viewport. */
function useNearViewport(ref, margin = "300px") {
  const [near, setNear] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: margin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, near, margin]);
  return near;
}

/**
 * Slide card cover: attached cover, else app-style `3/3/3` tile (centered +
 * slight zoom), else a generated cover. Only resolves near the viewport.
 */
export default function SlideCardThumb({
  slide,
  preferredUrl = null,
  alt = "",
  height,
  width,
  minWidth,
  emptyLabel = null,
  objectPosition = "center",
  dark = false,
}) {
  const frameRef = useRef(null);
  const near = useNearViewport(frameRef);
  const fileKey = useMemo(() => {
    const files = slideFiles(slide).map((f) => f?.url || f?.name || "").join(",");
    return `${COVER_CACHE_VERSION}|${files}|${preferredUrl || ""}`;
  }, [slide, preferredUrl]);
  const hasFiles = Boolean(slideFileFromSlide(slide));

  const [cover, setCover] = useState(() => coverCache.get(fileKey) ?? null);
  const [ready, setReady] = useState(() => coverCache.has(fileKey));

  useEffect(() => {
    if (coverCache.has(fileKey)) {
      setCover(coverCache.get(fileKey));
      setReady(true);
      return undefined;
    }
    setReady(false);
    setCover(null);
    if (!near) return undefined;
    let cancelled = false;
    resolveCover(slide, preferredUrl).then((result) => {
      coverCache.set(fileKey, result);
      if (cancelled) return;
      setCover(result);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fileKey, slide, preferredUrl, near]);

  const frameSx = {
    position: "relative",
    width: width ?? "100%",
    minWidth,
    height,
    overflow: "hidden",
    margin: 0,
    padding: 0,
    lineHeight: 0,
    flexShrink: 0,
    bgcolor: dark ? "#16181c" : "#F8FAFC",
  };

  let content;
  if (!ready) content = <CoverLoader dark={dark} />;
  else if (!cover) content = <GeneratedCover title={alt} dark={dark} label={hasFiles ? null : emptyLabel} />;
  else
    content = (
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            // Match app FastImage resizeMode=cover: fill the frame from the
            // center. Extra scale = the "little zoom" so tile edges/padding clip.
            transform: cover.zoomed ? "scale(1.18)" : "scale(1.4)",
            transformOrigin: "center center",
          }}
        >
          <LoadingImage
            src={cover.src}
            alt={alt}
            dark={dark}
            objectPosition={objectPosition}
            fallback={<GeneratedCover title={alt} dark={dark} />}
          />
        </Box>
      </Box>
    );

  return (
    <Box ref={frameRef} sx={frameSx} className="titles-card-thumb">
      {content}
    </Box>
  );
}
